import { Sparkles, BookOpen } from "lucide-react";
import type { CatalogItem, LibraryItem } from "../types/navigator";
import { LikeButton } from "./LikeButton";

type Props = {
  items: LibraryItem[];
  loading: boolean;
  error: string | null;
  personalized: boolean;
  matchedOn: string | null;
  popularItems: LibraryItem[];
  onOpen: (item: CatalogItem) => void;
};

function ForYouCard({
  item,
  onOpen,
}: {
  item: LibraryItem;
  onOpen: (item: CatalogItem) => void;
}) {
  return (
    <button
      key={item.id}
      className="foryou-card"
      onClick={() => onOpen(item)}
      aria-label={`Open ${item.title}`}
    >
      <div className="foryou-card-head">
        <span className="foryou-card-icon" aria-hidden>
          <BookOpen size={14} strokeWidth={1.75} />
        </span>
        <span className="foryou-card-kind">Library</span>
        {item.assetType ? (
          <span className="foryou-card-type">{item.assetType}</span>
        ) : null}
      </div>
      <div className="foryou-card-title">{item.title}</div>
      {item.description ? (
        <div className="foryou-card-desc">{item.description}</div>
      ) : null}
      <div className="foryou-card-foot">
        {item.department ? (
          <span className="foryou-card-dept">{item.department}</span>
        ) : null}
        <span className="foryou-card-like-wrap">
          <LikeButton item={item} />
        </span>
      </div>
    </button>
  );
}

function ForYouLane({
  title,
  subtitle,
  items,
  loading,
  error,
  emptyMessage,
  onOpen,
}: {
  title: string;
  subtitle: string;
  items: LibraryItem[];
  loading: boolean;
  error: string | null;
  emptyMessage: string;
  onOpen: (item: CatalogItem) => void;
}) {
  return (
    <div className="foryou-lane">
      <div className="foryou-lane-head">
        <h3 className="foryou-lane-title">{title}</h3>
        <p className="dash-section-sub">{subtitle}</p>
      </div>
      {error ? (
        <div className="dash-strip-empty">{error}</div>
      ) : loading ? (
        <div className="foryou-strip">
          {[0, 1, 2, 3].map((i) => (
            <div className="foryou-card foryou-skeleton" key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="dash-strip-empty">{emptyMessage}</div>
      ) : (
        <div className="foryou-strip">
          {items.map((item) => (
            <ForYouCard key={item.id} item={item} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ForYouRail({
  items,
  loading,
  error,
  personalized,
  matchedOn,
  popularItems,
  onOpen,
}: Props) {
  const roleSubtitle = personalized
    ? matchedOn
      ? `Matched on ${matchedOn}.`
      : "Items related to your team and role."
    : "Recently added to the Library.";

  return (
    <section className="dash-section">
      <div className="dash-section-head">
        <h2 className="dash-section-title">
          <Sparkles size={16} /> You might like
        </h2>
      </div>

      <ForYouLane
        title="Relevant to your Role"
        subtitle={roleSubtitle}
        items={items}
        loading={loading}
        error={error}
        emptyMessage="Nothing to recommend yet — your library is still being curated."
        onOpen={onOpen}
      />

      <ForYouLane
        title="Popular in the Org"
        subtitle="The most-liked items across Brookfield."
        items={popularItems}
        loading={loading}
        error={error}
        emptyMessage="No likes yet — be the first to like an item."
        onOpen={onOpen}
      />
    </section>
  );
}
